import debounce from 'lodash/fp/debounce'
import { action, computed, makeObservable, observable, reaction } from 'mobx'

import type AppStore from '@src/app/AppStore'
import PhoneNumberSelectorController from '@src/component/phone-number-selector/PhoneNumberSelectorController'
import isNonNull from '@src/lib/isNonNull'
import type { Identity, PhoneNumberModel } from '@src/service/model'
import { compareByName, compareMembersByPresence, MemberModel } from '@src/service/model'
import { ContactModel } from '@src/service/model/contact/ContactModel'
import type { ActiveCall } from '@src/service/voice'

export default abstract class VoicePhoneNumberSelectorController extends PhoneNumberSelectorController {
  contacts: ContactModel[] = []

  constructor(
    protected readonly app: AppStore,
    protected readonly call?: ActiveCall,
  ) {
    super()
    makeObservable(this, {
      contacts: observable,
      currentUser: computed,
      currentPhoneNumber: computed,
      currentPhoneNumberMembers: computed,
      phoneNumbers: computed,
      members: computed,
      unselectableMembers: computed,
      unselectableContacts: computed,
      hasExternalSelections: computed,
    })

    this.disposeBag.add(
      reaction(
        () => this.search,
        debounce(200)(() => {
          // eslint-disable-next-line @typescript-eslint/no-floating-promises -- UXP-3744 - Fix Promise-related ESLint issues
          this.app.service.search.identities(this.search, 20, 'contacts').then(
            action((result) => {
              this.contacts = result
                .map((c) => c.identity)
                .filter(
                  (contact): contact is ContactModel =>
                    contact instanceof ContactModel &&
                    !this.unselectableContacts.has(contact),
                )
            }),
          )
        }),
        { name: 'VoicePhoneNumberSelectorController.SearchChanged' },
      ),
      reaction(
        () => this.call?.participants.list,
        () => {
          this.replaceRows()
        },
        { name: 'VoicePhoneNumberSelectorController.ParticipantsLengthChanged' },
      ),
    )
  }

  get currentUser() {
    return this.app.service.user.current?.asMember ?? null
  }

  get currentPhoneNumber(): PhoneNumberModel | null {
    return this.call?.phoneNumber ?? null
  }

  get exclusions(): Identity[] {
    const exclusions: Identity[] = []

    if (this.mode === 'multiple') {
      // In multiple mode we want to exclude identities that are already selected to prevent them
      // from being selected twice
      exclusions.push(...this.selectedRows.map((row) => row.identity).filter(isNonNull))
    }

    if (this.call) {
      // Exclude identities that are already in the call
      exclusions.push(
        ...this.call.participants.list.map((p) => p.identity).filter(isNonNull),
      )
    }

    return exclusions
  }

  get currentPhoneNumberMembers(): MemberModel[] {
    return (
      this.call?.phoneNumber?.members
        .filter((member) => !this.unselectableMembers.has(member))
        .sort(compareMembersByPresence) ?? []
    )
  }

  get hasExternalSelections(): boolean {
    return this.selections.some(
      (selection) => selection.type !== 'member' && selection.type !== 'inbox-member',
    )
  }

  get hasInternalSelections(): boolean {
    return this.selections.some(
      (selection) =>
        selection.type === 'member' ||
        selection.type === 'inbox-member' ||
        selection.type === 'inbox',
    )
  }

  get phoneNumbers() {
    return this.app.service.phoneNumber.collection.list
      .filter((pn) => pn !== this.currentPhoneNumber && pn.isShared)
      .sort(compareByName)
  }

  get members() {
    return this.app.service.member.collection.list
      .filter((member) => {
        return member.phones.length > 0 && !this.exclusions.includes(member)
      })
      .sort(compareMembersByPresence)
  }

  get unselectableMembers() {
    const selectedMembers = this.selections
      .map((selection) => (selection.to instanceof MemberModel ? selection.to : null))
      .filter(isNonNull)

    return new Set([this.currentUser, ...selectedMembers])
  }

  get unselectableContacts() {
    const selectedContacts = this.selections
      .map((selection) => (selection.to instanceof ContactModel ? selection.to : null))
      .filter(isNonNull)

    return new Set(selectedContacts)
  }
}
